import { useCallback, useEffect, useRef, useState } from "react";
import s from "./followups.module.css";

/** How long a done or snooze waits before it is sent. The request does not go out while the Undo bar is up (brief → Tests). */
export const undoConfig = { ms: 5000 };

interface Entry { id: string; label: string; timer: ReturnType<typeof setTimeout>; commit: () => void; undo: () => void }

/**
 * Deferred writes with an Undo bar. `schedule` shows the bar and starts the clock; if the person presses Undo the write is
 * never sent. Anything still waiting when the page closes is sent at once, so leaving never loses a "done".
 */
export function useUndoQueue() {
  const [items, setItems] = useState<Array<{ id: string; label: string }>>([]);
  const entries = useRef(new Map<string, Entry>());

  const drop = useCallback((id: string) => {
    entries.current.delete(id);
    setItems((x) => x.filter((i) => i.id !== id));
  }, []);

  const schedule = useCallback((id: string, label: string, commit: () => void, undo: () => void) => {
    const run = () => { const e = entries.current.get(id); if (!e) return; clearTimeout(e.timer); drop(id); e.commit(); };
    clearTimeout(entries.current.get(id)?.timer);
    const timer = setTimeout(run, undoConfig.ms);
    entries.current.set(id, { id, label, timer, commit, undo });
    setItems((x) => [...x.filter((i) => i.id !== id), { id, label }]);
  }, [drop]);

  const undo = useCallback((id: string) => {
    const e = entries.current.get(id);
    if (!e) return;
    clearTimeout(e.timer);
    drop(id);
    e.undo();
  }, [drop]);

  useEffect(() => {
    const map = entries.current;
    return () => { for (const e of map.values()) { clearTimeout(e.timer); e.commit(); } map.clear(); };
  }, []);

  return { items, schedule, undo };
}

export function UndoBar({ items, onUndo }: { items: Array<{ id: string; label: string }>; onUndo: (id: string) => void }) {
  return (
    <div className={s.undoWrap} role="status" aria-live="polite">
      {items.map((i) => (
        <div className={s.undo} key={i.id} data-testid="undo-toast">
          <span>{i.label}</span>
          <button type="button" onClick={() => onUndo(i.id)}>Undo</button>
        </div>
      ))}
    </div>
  );
}
